import { Icon } from './Icon';
import estilos from './Paginacion.module.css';

interface PaginacionProps {
  pagina: number;
  porPagina: number;
  total: number;
  onCambiar: (pagina: number) => void;
}

/**
 * Números de página a mostrar, con puntos suspensivos cuando hay muchas.
 * Siempre incluye la primera, la última y las vecinas de la actual.
 */
function calcularNumeros(actual: number, ultima: number): (number | 'puntos')[] {
  if (ultima <= 7) return Array.from({ length: ultima }, (_, i) => i + 1);

  const numeros = new Set<number>([1, ultima, actual, actual - 1, actual + 1]);
  const visibles = [...numeros].filter((n) => n >= 1 && n <= ultima).sort((a, b) => a - b);

  const resultado: (number | 'puntos')[] = [];
  let anterior = 0;

  for (const n of visibles) {
    if (anterior && n - anterior > 1) resultado.push('puntos');
    resultado.push(n);
    anterior = n;
  }

  return resultado;
}

/** Paginación de una tabla o lista. */
export function Paginacion({ pagina, porPagina, total, onCambiar }: PaginacionProps) {
  const ultima = Math.max(1, Math.ceil(total / porPagina));
  if (total === 0) return null;

  const primero = (pagina - 1) * porPagina + 1;
  const ultimo = Math.min(pagina * porPagina, total);

  return (
    <nav className={estilos.contenedor} aria-label="Paginación">
      <div className={estilos.info}>
        <span className={estilos.destacado}>
          {primero}–{ultimo}
        </span>
        de {total}
      </div>

      <div className={estilos.controles}>
        <button
          type="button"
          className={estilos.boton}
          onClick={() => onCambiar(1)}
          disabled={pagina === 1}
          aria-label="Primera página"
        >
          <Icon nombre="primeraPagina" tamano={14} />
        </button>
        <button
          type="button"
          className={estilos.boton}
          onClick={() => onCambiar(pagina - 1)}
          disabled={pagina === 1}
          aria-label="Página anterior"
        >
          <Icon nombre="anterior" tamano={14} />
        </button>

        <div className={estilos.numeros}>
          {calcularNumeros(pagina, ultima).map((n, i) =>
            n === 'puntos' ? (
              <span key={`puntos-${i}`} className={estilos.puntos}>
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                className={[estilos.boton, n === pagina ? estilos.botonActivo : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onCambiar(n)}
                aria-current={n === pagina ? 'page' : undefined}
                aria-label={`Página ${n}`}
              >
                {n}
              </button>
            ),
          )}
        </div>

        <button
          type="button"
          className={estilos.boton}
          onClick={() => onCambiar(pagina + 1)}
          disabled={pagina >= ultima}
          aria-label="Página siguiente"
        >
          <Icon nombre="siguiente" tamano={14} />
        </button>
        <button
          type="button"
          className={estilos.boton}
          onClick={() => onCambiar(ultima)}
          disabled={pagina >= ultima}
          aria-label="Última página"
        >
          <Icon nombre="ultimaPagina" tamano={14} />
        </button>
      </div>
    </nav>
  );
}
